import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, startOfWeek } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import WeekPicker from "../Pickers/WeekPicker.jsx";
import { getSleepDataBuSubjectID } from "../../../services/apiCalls.js";
import SleepOverview from "./SleepOverview.jsx";
import SleepInsightsBarChart from "./SleepInsightsBarChart.jsx";
import { captureAndDownloadCharts } from "../CaptureCharts/captureAndDownloadCharts.js";
import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate.jsx";

export default function SleepComponent({
  selectedUser,
  tabsValue,
  setLoading,
}) {
  const [sleepData, setSleepData] = useState([]);
  const sleepOverview = useRef(null);
  const sleepInsights = useRef(null);

  const today = new Date();
  const [currentWeek, setCurrentWeek] = useState(today);
  const [todayClickFunc, setTodayClickFunc] = useState(null);

  const { t } = useTranslation("statistics");

  useEffect(() => {
    const fetchUserSleepData = async () => {
      try {
        const allSleepData = await getSleepDataBuSubjectID(selectedUser);
        const formattedSleepInsightsData = allSleepData.map((data) => ({
          date: format(new Date(data.start_time), "yyyy-MM-dd"),
          duration: data.duration,
          light: data.light,
          rem: data.rem,
          deep: data.deep,
          asleep: data.asleep,
          awake: data.awake,
          in_bed: data.in_bed,
        }));
        setSleepData(formattedSleepInsightsData);
      } catch (error) {
        console.error(
          "Error when fetching sleep data in sleep component",
          error
        );
      }
    };
    fetchUserSleepData();
  }, []);

  const handleTodayClickParent = (func) => {
    if (!todayClickFunc) {
      setTodayClickFunc(() => func);
    }
  };
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });

  const isCurrentWeek =
    weekStart.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime();

  const handleWeekChange = (week) => {
    setCurrentWeek(week);
    // console.log("Updated selected week:", week);
  };
  const weeksMonthsWithData = calculateWeeksAndMonthsWithData(sleepData);
  // console.log(
  //   "weeksMonthsWithData in sleepComponent: ",
  //   weeksMonthsWithData
  // );

  const weekStartISO = (d) =>
    format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

  const weeksWithDataSet = useMemo(() => {
    const s = new Set();
    for (const row of sleepData) {
      const day = new Date(row.date); // row.date is 'yyyy-MM-dd'
      s.add(weekStartISO(day));
    }
    return s;
  }, [sleepData]);

  const hasAnyData = sleepData.length > 0;
  const todayWeekISO = weekStartISO(today);
  const todayWeekHasData = weeksWithDataSet.has(todayWeekISO);

  const showTodayButton = !isCurrentWeek && (!hasAnyData || todayWeekHasData);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WeekPicker
          today={today}
          currentCompWeek={currentWeek}
          sendDataToParent={handleTodayClickParent}
          onWeekChange={handleWeekChange}
          weeksWithData={weeksMonthsWithData.weeksWithData}
          monthsWithData={weeksMonthsWithData.monthsWithData}

        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() =>
              captureAndDownloadCharts(
                "Sleep",
                tabsValue,
                { sleepOverview, sleepInsights },
                setLoading
              )
            }
            sx={{
              maxWidth: "210px",
              "&:hover": {
                backgroundColor: "white",
                color: "rgb(2, 178, 175)",
              },
            }}
          >
            {t("btn.downloadAllCharts")}
          </Button>
        </Box>
        {showTodayButton ? (
          <Button
            sx={{
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "white",
                color: "rgb(2, 178, 175)",
              },
            }}
            onClick={todayClickFunc}
          >
            {t("btn.today")}
          </Button>
        ) : (
          <Button
            sx={{
              visibility: "hidden",
            }}
          >
            {t("btn.today")}
          </Button>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
              lg: "row",
            },
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SleepOverview
            sleepData={sleepData}
            selectedWeek={currentWeek}
            sleepOverview={sleepOverview}
          />
          <SleepInsightsBarChart
            sleepData={sleepData}
            selectedWeek={currentWeek}
            sleepInsights={sleepInsights}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
